/**
 * /cli-auth 授权页的「登录后回跳」通道。
 *
 * 现有登录流程(短信/飞书/微信)成功后一律跳固定的 defaultRoute,没有 redirect
 * 参数机制;而 bujuan CLI 一键授权要求"未登录 → 正常登录 → 带原 query 回到 /cli-auth"。
 * 用 sessionStorage 存回跳地址:tab 级隔离、飞书 OAuth 跳出再回来仍在、不污染 URL。
 *
 * 安全:只接受以 /cli-auth 开头的站内路径,杜绝开放重定向;10 分钟过期,
 * 避免中途放弃的授权流残留标记、把日后同 tab 的正常登录劫持回已死的 callback。
 * 消费方式:登录出口只「读」(getCliAuthReturn),/cli-auth 页登录态下负责「清」
 * (clearCliAuthReturn)——读不删,天然兼容 React StrictMode 双渲染。
 *
 * 保存时机:main.tsx 入口处(saveCliAuthReturnFromLocation)。必须在应用启动
 * 最早期保存,因为 localStorage 里若残留已失效 token,App 层 useUser 会先发请求、
 * 401 后被 apiClient logout() 硬跳 /login,/cli-auth 页面根本来不及挂载。
 */
const KEY = "cli-auth:return";

/** 回跳标记有效期:覆盖一次完整登录(含收短信验证码),又不至于长期残留 */
const TTL_MS = 10 * 60 * 1000;

/** 记下 /cli-auth 的完整站内地址(path + query),供登录成功后回跳 */
export const saveCliAuthReturn = (path: string) => {
	if (!path.startsWith("/cli-auth")) return;
	try {
		sessionStorage.setItem(KEY, JSON.stringify({ path, ts: Date.now() }));
	} catch {
		// sessionStorage 不可用(隐私模式等)时静默降级:登录后回默认页
	}
};

/** 应用入口调用:当前就在 /cli-auth 时立即保存,抢在任何 401 硬跳转之前 */
export const saveCliAuthReturnFromLocation = () => {
	if (window.location.pathname.startsWith("/cli-auth")) {
		saveCliAuthReturn(window.location.pathname + window.location.search);
	}
};

/** 读取待回跳地址;非 /cli-auth 开头或超过 10 分钟一律视为无效 */
export const getCliAuthReturn = (): string | null => {
	try {
		const raw = sessionStorage.getItem(KEY);
		if (!raw) return null;
		const { path, ts } = JSON.parse(raw) as { path?: string; ts?: number };
		if (!path?.startsWith("/cli-auth")) return null;
		if (!ts || Date.now() - ts > TTL_MS) return null;
		return path;
	} catch {
		return null;
	}
};

/** 回跳完成(/cli-auth 已在登录态下打开)后清除 */
export const clearCliAuthReturn = () => {
	try {
		sessionStorage.removeItem(KEY);
	} catch {
		// ignore
	}
};
